import axios from 'axios';

import iAxios from '../IAxios';
import api, { resolveAiChatUrl } from './api';
import type {
  AiChatCancelStreamReq,
  AiChatCancelStreamRsp,
  AiChatTaskStatusReq,
  AiChatTaskStatusRsp,
  ChatSessionDetailReq,
  ChatSessionDetailWire,
  ChatSessionRecordListReq,
  ChatSessionRecordListRsp,
  DeleteChatSessionsReq,
  DeleteChatSessionsRsp,
  GetCustomerServicePoliciesReq,
  GetCustomerServicePoliciesRsp,
  PinChatSessionReq,
  PinChatSessionRsp,
  QueryExperiencesReq,
  QueryExperiencesRsp,
  ShareChatSessionReq,
  ShareChatSessionRsp,
  TrainingProposalConfirmReq,
  TrainingProposalConfirmRsp,
  TrainingProposalQuery,
  TrainingProposalRsp,
  UpdateChatSessionNameReq,
  UpdateChatSessionNameRsp,
} from './types';

const getResponseError = async (response: Response) => {
  const responseText = await response.text().catch(() => '');
  return new Error(
    `HTTP ${response.status}: ${responseText || response.statusText}`
  );
};

export const confirmTrainingProposal = (
  data: TrainingProposalConfirmReq,
  signal?: AbortSignal
) =>
  iAxios.request<TrainingProposalConfirmRsp>({
    url: api.confirmProposal,
    method: 'post',
    data,
    signal,
    // 成功正文就是提案本身。默认拆 data.data 会得到 undefined，accepted 写不回去，选项就还在。
    rawResponseData: true,
  });

/** 有 turn_id 才写入；空串省略，避免后端收到空 turn。 */
export const buildCancelStreamReq = (
  sessionId: string,
  appId: string,
  userId: string,
  turnId?: string
): AiChatCancelStreamReq => {
  const body: AiChatCancelStreamReq = {
    session_id: sessionId,
    user_id: userId,
    app_id: appId,
  };
  const trimmedTurnId = turnId?.trim();
  if (trimmedTurnId) body.turn_id = trimmedTurnId;
  return body;
};

/** 取消当前 agent SSE 轮次。POST /aisee/agent/v2/sse/agent/cancel。 */
export const cancelAgentStream = (data: AiChatCancelStreamReq) =>
  iAxios.request<AiChatCancelStreamRsp>({
    url: api.cancelStream,
    method: 'post',
    data,
    requireFieldCheck: {
      fields: ['session_id', 'app_id', 'user_id'],
    },
  });

export const getTrainingProposal = (params: TrainingProposalQuery) =>
  iAxios.request<TrainingProposalRsp>({
    url: api.queryProposal,
    method: 'get',
    params,
    // 正文可能就是提案，没有 data 包一层。默认拆包会把它变成 undefined，状态就丢了。
    rawResponseData: true,
  });

const readExperiencePage = (value: unknown): QueryExperiencesRsp | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.items)) return null;
  const nextCursor = record.next_cursor;
  return {
    items: record.items as QueryExperiencesRsp['items'],
    total: typeof record.total === 'number' ? record.total : 0,
    next_cursor:
      typeof nextCursor === 'string' && nextCursor ? nextCursor : null,
  };
};

/** 全局规则。GET /aisee/v2/agent/policies，对应 GetCustomerServicePolicies。 */
export const getCustomerServicePolicies = (
  params: GetCustomerServicePoliciesReq
) =>
  iAxios.request<GetCustomerServicePoliciesRsp>({
    url: api.customerServicePolicies,
    method: 'get',
    params,
    requireFieldCheck: {
      fields: ['app_id', 'mode'],
    },
  });

/** 查询经验。GET /aisee/v2/training/query-experiences。正文可能直接是 items，也可能包在 data 里。 */
export const queryExperiences = async (params: QueryExperiencesReq) => {
  const body = await iAxios.request<unknown>({
    url: api.queryExperience,
    method: 'get',
    params,
    rawResponseData: true,
    requireFieldCheck: {
      fields: ['app_id'],
    },
  });
  const nested =
    body && typeof body === 'object'
      ? (body as { data?: unknown; Data?: unknown })
      : undefined;
  return (
    readExperiencePage(body) ??
    readExperiencePage(nested?.data) ??
    readExperiencePage(nested?.Data) ?? {
      items: [],
      total: 0,
      next_cursor: null,
    }
  );
};

/**
 * 提案还没被采纳。只认 HTTP 404 且正文 code 为 PROPOSAL_NOT_FOUND。
 * 以前任意 404、或非 404 但带这个 code 也算，现在收窄。500 和网络错误不算。
 */
export const isTrainingProposalNotFound = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;
  const { response } = error;
  if (response?.status !== 404) return false;
  const { data } = response;
  if (!data || typeof data !== 'object') return false;
  return (data as { code?: unknown }).code === 'PROPOSAL_NOT_FOUND';
};

export const getAiChatTaskStatus = async (
  params: AiChatTaskStatusReq,
  signal?: AbortSignal,
  taskStatusPath = api.taskStatus
): Promise<AiChatTaskStatusRsp> => {
  const query = new URLSearchParams({
    session_id: params.session_id,
    app_id: params.app_id,
  });
  const response = await fetch(
    `${resolveAiChatUrl(taskStatusPath)}?${query.toString()}`,
    { signal }
  );

  if (!response.ok) throw await getResponseError(response);
  return response.json() as Promise<AiChatTaskStatusRsp>;
};

/** 会话列表。POST /aisee/v2/agent/session/list。 */
export const getChatSessionRecords = (
  data: ChatSessionRecordListReq,
  signal?: AbortSignal
) =>
  iAxios.request<ChatSessionRecordListRsp>({
    url: api.sessionRecords,
    method: 'post',
    data,
    signal,
  });

/** 会话详情。GET /aisee/v2/agent/session/detail。 */
export const getChatSessionDetail = (params: ChatSessionDetailReq) =>
  iAxios.request<ChatSessionDetailWire>({
    url: api.sessionDetail,
    method: 'get',
    params,
  });

/** 修改会话名称。POST /aisee/v2/agent/session/updateName。 */
export const updateChatSessionName = (data: UpdateChatSessionNameReq) =>
  iAxios.request<UpdateChatSessionNameRsp>({
    url: api.sessionUpdateName,
    method: 'post',
    data,
    requireFieldCheck: {
      fields: ['app_id', 'session_id', 'title'],
    },
  });

/** 删除会话。POST /aisee/v2/agent/session/del。单条也传 session_ids。 */
export const deleteChatSessions = (data: DeleteChatSessionsReq) =>
  iAxios.request<DeleteChatSessionsRsp>({
    url: api.sessionDelete,
    method: 'post',
    data,
    requireFieldCheck: {
      fields: ['app_id', 'session_ids'],
    },
  });

/** 置顶或取消置顶。POST /aisee/v2/agent/session/pin。 */
export const pinChatSession = (data: PinChatSessionReq) =>
  iAxios.request<PinChatSessionRsp>({
    url: api.sessionPin,
    method: 'post',
    data,
    requireFieldCheck: {
      fields: ['app_id', 'session_id', 'pinned'],
    },
  });

/** 分享会话。POST /aisee/v2/agent/session/share。reset 为 true 时作废旧链接。返回 share: token。 */
export const shareChatSession = (data: ShareChatSessionReq) =>
  iAxios.request<ShareChatSessionRsp>({
    url: api.sessionShare,
    method: 'post',
    data,
  });

export * from './types';
